import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Check, Loader2, TrendingUp, CreditCard, Home, User, Settings, LogOut } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/utils/apiConfig";
import Footer from "@/components/Footer";
import LandingNavbar from "@/components/LandingNavbar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";

function UpgradePage({ selectedPackage = null }) {
  const { user, token, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(selectedPackage);
  // COMMIT-B: upgrade onay modali. upgradeModal = { planKey, preview } | null. Tutar BACKEND preview'dan
  //   gelir (frontend HESAPLAMAZ - tek-kaynak Stripe). upgradeSubmitting = onay POST'u sirasinda.
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [upgradeSubmitting, setUpgradeSubmitting] = useState(false);

  // Preview tutarlarini goster (cents + ISO currency). Frontend YALNIZ formatlar, HESAPLAMAZ.
  const fmtMoney = (cents, currency) => {
    const val = (Number(cents || 0) / 100).toFixed(2);
    return `${val} ${(currency || "usd").toUpperCase()}`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric" }); }
    catch (_) { return "-"; }
  };

  // REMOVED: Allow non-logged-in users to view packages
  // They will see login required toast when trying to purchase

  // FAZ-2 TEK-KAYNAK (11.9): paketler /api/packages'ten (hardcoded getPlans KALDIRILDI). features_en tek kaynak.
  //   Alan-eslesme: price=$price_usd, type=mode('subscription'->'recurring'), features=features_en. Tek-sefer fetch, t YOK.
  const [plans, setPlans] = useState({});
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/packages`);
        const map = {};
        (res?.data?.packages || []).forEach((p) => {
          map[p.package_id] = {
            name: p.name,
            price: `$${Number(p.price_usd).toLocaleString("en-US")}`,
            credits: p.credits,
            type: p.mode === "subscription" ? "recurring" : "one_time",
            features: p.features_en || [],
          };
        });
        if (active) setPlans(map);
      } catch (e) {
        if (active) setPlans({});
      }
    })();
    return () => { active = false; };
  }, []);

  // Get locale-aware paths
  const getLocalePath = (route) => {
    const routes = {
      en: {
        home: '/en/',
        login: '/en/login',
        pricing: '/en/pricing',
        checkout: '/en/checkout',
        settings: '/en/settings',
        pro: '/en/pro-monthly',
        business: '/en/business-monthly',
        package10: '/en/package-10',
        package50: '/en/package-50',
        package100: '/en/package-100'
      },
      tr: {
        home: '/tr/',
        login: '/tr/giris',
        pricing: '/tr/kredi-paketleri',
        checkout: '/tr/odeme',
        settings: '/tr/hesap-ayarlari',
        package10: '/tr/paket-10',
        package50: '/tr/paket-50',
        package100: '/tr/paket-100'
      },
      ar: {
        home: '/ar/',
        login: '/ar/login',
        pricing: '/ar/pricing',
        checkout: '/ar/checkout',
        settings: '/ar/settings',
        pro: '/ar/pro-monthly',
        business: '/ar/business-monthly',
        package10: '/ar/package-10',
        package50: '/ar/package-50',
        package100: '/ar/package-100'
      },
      de: {
        home: '/de/',
        login: '/de/login',
        pricing: '/de/pricing',
        checkout: '/de/checkout',
        settings: '/de/settings',
        pro: '/de/pro-monthly',
        business: '/de/business-monthly',
        package10: '/de/package-10',
        package50: '/de/package-50',
        package100: '/de/package-100'
      },
      fr: {
        home: '/fr/',
        login: '/fr/login',
        pricing: '/fr/pricing',
        checkout: '/fr/checkout',
        settings: '/fr/settings',
        pro: '/fr/pro-monthly',
        business: '/fr/business-monthly',
        package10: '/fr/package-10',
        package50: '/fr/package-50',
        package100: '/fr/package-100'
      },
      es: {
        home: '/es/',
        login: '/es/login',
        pricing: '/es/pricing',
        checkout: '/es/checkout',
        settings: '/es/settings',
        pro: '/es/pro-monthly',
        business: '/es/business-monthly',
        package10: '/es/package-10',
        package50: '/es/package-50',
        package100: '/es/package-100'
      }
    };
    return routes[locale]?.[route] || routes.en[route];
  };

  // Helper function to get plan display name
  const getPlanDisplayName = (planType) => {
    const planNames = {
      "free": t('plans.free'),
      "package_daily": t('plans.package_daily'),
      "package_weekly": t('plans.package_weekly'),
      "package_monthly": t('plans.package_monthly'),
      "pro_monthly": t('plans.pro_monthly'),
      "business_monthly": t('plans.business_monthly'),
      "persona_staff": t('plans.persona_staff')
    };
    return planNames[planType] || planType;
  };

  // Logout function
  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear auth data regardless of API response
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate(getLocalePath('home'));
      window.location.reload();
    }
  };

  // Available packages - all users can buy any package - OPARA v7.4
  const getAvailablePlans = () => {
    return ["package_10", "package_50", "package_100", "pro_monthly", "business_monthly"];
  };

  const handleUpgrade = async (planKey, ctaMode) => {
    // Check if user is logged in
    if (!user) {
      toast.error(t('pricing.login_required'), {
        description: t('pricing.login_required_desc'),
        action: {
          label: t('auth.login.submit_btn'),
          onClick: () => navigate(getLocalePath('login'))
        }
      });
      return;
    }

    // COMMIT-B: UPGRADE (yuksek-tier + aktif abonelik) SESSIZ OLMAZ -> once BACKEND proration preview'ini
    //   al, GERCEK tutari onay modalinda goster; kullanici onaylayinca COMMIT-3 in-place modify cagrilir.
    //   Preview basarisizsa upgrade BASLATILMAZ (tutar gosterilemeden onay alinmaz). CheckoutPage'e GITME.
    //   Yeni-abonelik (userTier==0) ve kredi paketleri asagida BYTE-AYNI navigate ile devam eder.
    if (ctaMode === "upgrade") {
      try {
        setLoading(true);
        setSelectedPlan(planKey);
        const res = await axios.post(
          `${BACKEND_URL}/api/payments/upgrade-preview`,
          { package_id: planKey },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }  // withCredentials global default -> cookie yedek
        );
        setUpgradeModal({ planKey, preview: res.data });  // tutar BACKEND'den -> modal
      } catch (error) {
        // i18n-code'lu 400/403/409 (not_an_upgrade / sub_user / not_found_contact_support / preview_failed)
        //   detail {code,message} OBJE olabilir -> .message al, objeyi ham gosterme.
        const _d = error.response?.data?.detail;
        const _msg = (_d && typeof _d === "object") ? _d.message : _d;
        toast.error(_msg || t('upgrade.failed'));
      } finally {
        setLoading(false);
        setSelectedPlan(null);
      }
      return;
    }

    // Yeni abonelik / kredi paketi -> CheckoutPage (mevcut davranis, BYTE-AYNI)
    // CheckoutPage expects ?package=package_10, ?package=package_50, etc.
    const checkoutPath = getLocalePath('checkout');
    navigate(`${checkoutPath}?package=${planKey}`);
  };

  // COMMIT-B: modal "Onayla" -> COMMIT-3 upgrade endpoint (in-place modify). {upgraded:true} -> basari +
  //   /auth/me tazele (kart "Current Plan"a doner). client_secret DONMEZ -> embedded modal ACMA.
  const confirmUpgrade = async () => {
    if (!upgradeModal) return;
    const planKey = upgradeModal.planKey;
    try {
      setUpgradeSubmitting(true);
      const res = await axios.post(
        `${BACKEND_URL}/api/payments/create-checkout`,
        { package_id: planKey, origin_url: window.location.origin, locale: "en", ui_mode: "embedded", subscription_approval: true },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (res.data?.upgraded) {
        setUpgradeModal(null);
        toast.success(t('upgrade.success'));
        if (refreshUserData) { await refreshUserData(); }
      } else {
        toast.error(t('upgrade.failed'));
      }
    } catch (error) {
      const _d = error.response?.data?.detail;
      const _msg = (_d && typeof _d === "object") ? _d.message : _d;
      toast.error(_msg || t('upgrade.failed'));
    } finally {
      setUpgradeSubmitting(false);
    }
  };

  const availablePlans = getAvailablePlans();

  // COMMIT-4: mevcut abonelik + tier ile buton durumu (backend has_active_subscription ile ayni mantik:
  //   plan pro/business VE iptal-edilmemis). subscription_cancelled true ise (donem-sonu iptal) tekrar
  //   satin alma serbest kalmali -> aktif SAYILMAZ. Kredi paketleri (mode=payment) HER ZAMAN aktif.
  const TIER = { pro_monthly: 1, business_monthly: 2 };
  const userHasActiveSub = !!(user && ["pro_monthly", "business_monthly"].includes(user.plan_type) && !user.subscription_cancelled);
  const userTier = TIER[user?.plan_type] || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header - Mobil uyumlu */}
      <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-gray-900 to-black pt-2 pb-1 px-2 sm:px-4">
        <div className="mb-1 px-2 sm:px-4">
          <div className="flex justify-between items-center gap-2 sm:gap-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-2 border border-gray-800">
            {/* Sol: Logo - Tıklanabilir */}
            <div 
                  className="cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                  onClick={() => navigate(getLocalePath('home'))}
                >
                  <img 
                    src="/opara-logo-trimmed.png" 
                    alt="OPARA Logo" 
                    className="h-8 sm:h-10 w-auto object-contain"
                  />
                </div>
            
            {/* Right: Analysis Credits and Avatar (for logged in users) or Sign In button */}
            {user ? (
              <div className="flex items-center gap-1.5 sm:gap-2">
                {/* Analysis Credits - OPARA v8.0: Font size increased by 1 level */}
                <div className="text-sm sm:text-base bg-gray-800 px-2 sm:px-3 py-1 rounded-lg border border-gray-700">
                  <span className="text-white hidden sm:inline">{t('nav.credits')} </span>
                  <span className="text-white font-bold">
                    {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining}
                  </span>
                </div>
                
                {/* Avatar Dropdown Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer border-2 border-gray-700 hover:border-[#FFDD59]">
                      <User className="w-4 h-4 text-black" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700" align="end">
                    <DropdownMenuLabel className="text-white">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">
                          {user?.first_name} {user?.last_name}
                          {user?.plan_type === "persona_staff" && (
                            <span className="text-xs text-[#FFDD59] ml-2">{t('nav.staff_badge')}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">
                          Plan: <span className="text-[#FFDD59]">{getPlanDisplayName(user?.plan_type)}</span>
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem asChild className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800">
                      <Link to={getLocalePath('settings')} className="flex items-center">
                        <Settings className="w-4 h-4 mr-2" />
                        {t('nav.account_settings')}
                      </Link>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-gray-800">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              /* Sign In button for non-logged-in users */
              <Button 
                onClick={() => navigate(getLocalePath('login'))}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold w-40 py-1 text-sm"
              >
                {t('nav.signin')}
              </Button>
            )}
          </div>
        </div>
      </header>
      
      {/* Homepage Button - Below header */}
      <div className="px-4 mb-2">
        <div className="px-4">
          <Button 
            onClick={() => navigate(getLocalePath('home'))}
            className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
            data-testid="return-to-home-btn"
          >
            ← {t('nav.return_home')}
          </Button>
        </div>
      </div>
      
      <div className="relative z-10 py-4 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#FFDD59] mb-4">{t('pricing.title')}</h1>
            <p className="text-gray-300 text-lg">{t('pricing.subtitle')}</p>
            
            {/* Pro/Business users with exhausted quota can buy add-on packages */}
            {user && ["pro_monthly", "business_monthly"].includes(user.plan_type) && user.credits_remaining === 0 && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-4 max-w-2xl mx-auto">
                <p className="text-sm text-blue-300">
                  {t('upgrade.quota_exhausted')}
                </p>
              </div>
            )}
          </div>

          {/* All Packages - Mobilde tek sütun, tablet'te 2-3, desktop'ta 5 */}
          <div className="mb-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
              {availablePlans.map((planKey) => {
                const plan = plans[planKey];
                if (!plan) return null;  // FAZ-2: /api/packages fetch bitmeden render guard
                const isPopular = planKey === "pro_monthly";
                const isRecurring = plan.type === "recurring";

                // COMMIT-4: abonelik karti icin CTA durumu. Kredi paketi (isSubPlan=false) her zaman "buy".
                //   current -> ayni-tier: PASIF "Current Plan". upgrade -> yuksek-tier: aktif "Upgrade to X".
                //   downgrade -> dusuk-tier (Business iken Pro): PASIF (backend 403 downgrade_unsupported).
                //   (i18n: bu 3 CTA metni EN-only bu pass'te; TR/DE/FR/ES/AR ayri i18n kalemi.)
                const isSubPlan = planKey === "pro_monthly" || planKey === "business_monthly";
                const planTier = TIER[planKey] || 0;
                let ctaMode = "buy";
                if (userHasActiveSub && isSubPlan) {
                  if (planTier === userTier) ctaMode = "current";
                  else if (planTier > userTier) ctaMode = "upgrade";
                  else ctaMode = "downgrade";
                }
                const ctaDisabled = loading || ctaMode === "current" || ctaMode === "downgrade";

                return (
                  <Card
                    key={planKey}
                    className={`bg-gray-900 border-2 ${
                      isPopular ? "border-[#FFDD59]" : "border-gray-800"
                    } ${isRecurring ? "ring-2 ring-purple-500/50" : ""} relative`}
                  >
                    {isPopular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FFDD59] text-black px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap">
                        {t('pricing.recommended')}
                      </div>
                    )}
                    
                    {isRecurring && plan.badge && (
                      <div className="absolute -top-3 right-2 sm:right-4">
                        <span className="bg-purple-600 text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full">
                          {t('pricing.auto_renew')}
                        </span>
                      </div>
                    )}
                    
                    <CardHeader className="text-center pb-2 sm:pb-4">
                      <CardTitle className="text-xl sm:text-2xl text-white">{plan.name}</CardTitle>
                      <div className="mt-2 sm:mt-4">
                        <div>
                          <div className="text-3xl sm:text-4xl font-bold text-[#FFDD59]">{plan.price}</div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-300">
                            <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0 mt-0.5" />
                            {typeof feature === 'object' && feature.main ? (
                              <div className="flex flex-col">
                                <span className="text-xs sm:text-sm font-semibold text-white">{feature.main}</span>
                                <span className="text-xs sm:text-sm text-[#FFDD59]">{feature.sub}</span>
                              </div>
                            ) : (
                              <span className="text-xs sm:text-sm">{feature}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                      
                      {ctaMode === "current" && (
                        <div className="w-full text-center mb-2">
                          <span className="inline-block bg-[#FFDD59]/15 text-[#FFDD59] text-xs font-bold px-3 py-1 rounded-full">
                            Current Plan
                          </span>
                        </div>
                      )}
                      <Button
                        onClick={() => handleUpgrade(planKey, ctaMode)}
                        disabled={ctaDisabled}
                        className={`w-full font-bold text-sm sm:text-base ${
                          ctaDisabled
                            ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                            : isPopular
                              ? "bg-[#FFDD59] hover:bg-[#E6C84D] text-black"
                              : "bg-gray-800 hover:bg-gray-700 text-white"
                        }`}
                      >
                        {loading && selectedPlan === planKey ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('pricing.buying')}
                          </>
                        ) : ctaMode === "current" ? (
                          "Current Plan"
                        ) : ctaMode === "upgrade" ? (
                          `Upgrade to ${plan.name}`
                        ) : ctaMode === "downgrade" ? (
                          "Included in your plan"
                        ) : (
                          t('pricing.buy_btn').replace('{plan_name}', plan.name)
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Payment Logos Section - OPARA v7.3 Global Gateway */}
          <div className="mt-12 flex flex-col items-center gap-6">
            {/* Global Payment Message */}
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-gray-300">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">Secure Payment via Stripe</span>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="mt-12 bg-gray-900/50 backdrop-blur-sm rounded-lg p-6 border border-gray-800">
            <h3 className="text-lg font-semibold text-[#FFDD59] mb-4">📋 {t('upgrade.faq_title')}</h3>
            <div className="space-y-3 text-sm text-gray-300">
              {/* Kredi-dili acilis notu (ilk sira, belirgin) */}
              <p className="font-semibold text-white bg-[#FFDD59]/10 border border-[#FFDD59]/30 rounded-md px-3 py-2">
                💡 {t('upgrade.faq_credit_note')}
              </p>
              <p>
                <span className="text-[#FFDD59] font-semibold">{t('upgrade.faq_payg_label')}</span> {t('upgrade.faq_payg_desc')}
              </p>
              
              <p>
                <span className="text-[#FFDD59] font-semibold">{t('upgrade.faq_sub_label')}</span> {t('upgrade.faq_sub_desc')}
              </p>
              
              <p>
                <span className="text-[#FFDD59] font-semibold">{t('upgrade.faq_log_label')}</span> {t('upgrade.faq_log_desc')}
              </p>
              
              <p>
                <span className="text-[#FFDD59] font-semibold">{t('upgrade.faq_team_label')}</span> {t('upgrade.faq_team_desc')}
              </p>
              
              <p>
                <span className="text-[#FFDD59] font-semibold">{t('upgrade.faq_secure_label')}</span> {t('upgrade.faq_secure_desc')}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />

      {/* COMMIT-B: UPGRADE ONAY MODALI - tutar BACKEND preview'dan (frontend HESAPLAMAZ). Tum metinler i18n-code. */}
      {upgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-4">
              {t('upgrade.confirm_title').replace('{plan_name}', upgradeModal.preview?.target_plan_name || '')}
            </h3>
            <div className="text-sm text-gray-300 space-y-3 mb-6">
              <p>
                {t('upgrade.confirm_proration')
                  .replace('{amount}', fmtMoney(upgradeModal.preview?.proration_amount, upgradeModal.preview?.currency))
                  .replace('{total}', fmtMoney(upgradeModal.preview?.next_invoice_total, upgradeModal.preview?.currency))}
              </p>
              <p>
                {t('upgrade.confirm_renewal')
                  .replace('{amount}', fmtMoney(upgradeModal.preview?.next_renewal_amount, upgradeModal.preview?.currency))
                  .replace('{date}', fmtDate(upgradeModal.preview?.next_renewal_date))}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setUpgradeModal(null)}
                disabled={upgradeSubmitting}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white"
              >
                {t('upgrade.confirm_cancel')}
              </Button>
              <Button
                onClick={confirmUpgrade}
                disabled={upgradeSubmitting}
                className="flex-1 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
              >
                {upgradeSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  t('upgrade.confirm_ok')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpgradePage;
